import React, {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import AppContainer from "../../common/app_container";
import Alert, {AlertProps} from "../../common/alert";
import {SubmitHandler, useForm} from "react-hook-form";
import {InvitationCode, InvitationCodeFormData} from "@src/model/invitation_code";
import {useInvitationCodeAPI} from "@src/api/invitation_code_api";

const InvitationCodeEdit: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { getCode, createCode, updateCode } = useInvitationCodeAPI();
    const [status, setStatus] = useState<AlertProps | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isFetching, setIsFetching] = useState<boolean>(!!id);
    const isEditMode = !!id;

    const { register, handleSubmit, reset, watch, formState: { errors, isDirty } } = useForm<InvitationCodeFormData>();

    useEffect(() => {
        if (isEditMode) {
            setIsFetching(true);
            setStatus(null);
            getCode(id!)
                .then(codeData => {
                    const formData: InvitationCodeFormData = {
                        code: codeData.code,
                        status: codeData.status,
                        max_usages: codeData.max_usages,
                    };
                    reset(formData);
                })
                .catch((err: any) => setStatus({ type: "error", message: `Failed to load code: ${err.message}` }))
                .finally(() => setIsFetching(false));
        }
    }, [id]);

    const onSubmit: SubmitHandler<InvitationCodeFormData> = async (formData) => {
        setIsLoading(true);
        setStatus(null);
        const payload: Partial<InvitationCodeFormData> = {
            ...formData,
            max_usages: formData.max_usages !== undefined ? Number(formData.max_usages) : 0,
        };

        try {
            let result: InvitationCode;
            if (isEditMode) {
                result = await updateCode(id!, payload);
                setStatus({ type: "success", message: "Code updated successfully." });
                reset(payload);
            } else {
                if (!payload.code) throw new Error("Code is required for creation.");
                const createPayload: InvitationCodeFormData = {
                    code: payload.code,
                    max_usages: payload.max_usages,
                    status: payload.status || "ACTIVE",
                };
                result = await createCode(createPayload);
                setStatus({ type: "success", message: "Code created successfully." });
                navigate(`/invitation-codes/edit/${result.id}`, { replace: true });
            }
        } catch (error: any) {
            setStatus({ type: "error", message: `Failed to save code: ${error.message}` });
        } finally {
            setIsLoading(false);
        }
    };

    const currentCodeData = watch();

    if (isFetching) {
        return <AppContainer><div className="p-6">Loading code details...</div></AppContainer>;
    }

    return (
        <AppContainer>
            <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                    <h1 className="text-2xl font-semibold">{isEditMode ? "Edit Invitation Code" : "Create Invitation Code"}</h1>
                    <button
                        onClick={() => navigate("/invitation-codes")}
                        className="px-4 py-2 bg-gray-500 text-white text-sm rounded hover:bg-gray-700"
                    >
                        Back to List
                    </button>
                </div>

                {status && <Alert {...status} onClose={() => setStatus(null)} className="mb-4" />}

                <form onSubmit={handleSubmit(onSubmit)} className="bg-white p-6 rounded shadow border space-y-4">
                    <div>
                        <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">Invitation Code</label>
                        <input
                            type="text"
                            id="code"
                            {...register("code", { 
                                required: "Code is required",
                                onChange:(e) => {
                                  e.target.value = e.target.value.toUpperCase();
                              }                            })}
                            className={`block w-full p-2 border rounded shadow-sm text-sm ${errors.code ? "border-red-500" : "border-gray-300"} focus:ring-blue-500 focus:border-blue-500`}
                            placeholder="E.g., TALENT2025"
                            readOnly={isEditMode}
                        />
                        {errors.code?.message && <p className="text-red-500 text-xs mt-1">{String(errors.code.message)}</p>}
                        {isEditMode && <p className="text-xs text-gray-500 mt-1">Code cannot be changed after creation.</p>}
                    </div>

                    {isEditMode && (
                        <div>
                            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                            <select
                                id="status"
                                {...register("status")}
                                className={`block w-full p-2 border rounded shadow-sm text-sm ${errors.status ? "border-red-500" : "border-gray-300"} focus:ring-blue-500 focus:border-blue-500`}
                            >
                                <option value="ACTIVE">ACTIVE</option>
                                <option value="INACTIVE">INACTIVE</option>
                            </select>
                            {errors.status && <p className="text-red-500 text-xs mt-1">{String(errors.status.message)}</p>}
                        </div>
                    )}

                    <div>
                        <label htmlFor="max_usages" className="block text-sm font-medium text-gray-700 mb-1">Max Usages (0 for unlimited)</label>
                        <input
                            type="number"
                            id="max_usages"
                            min="0"
                            {...register("max_usages", { valueAsNumber: true, min: { value: 0, message: "Must be 0 or greater" } })}
                            className={`block w-full p-2 border rounded shadow-sm text-sm ${errors.max_usages ? "border-red-500" : "border-gray-300"} focus:ring-blue-500 focus:border-blue-500`}
                            placeholder="1000"
                        />
                        {errors.max_usages?.message && <p className="text-red-500 text-xs mt-1">{String(errors.max_usages.message)}</p>}
                    </div>

                    {isEditMode && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Current Usages</label>
                            <p className="text-sm text-gray-800 mt-1">N/A (Load separately)</p>
                        </div>
                    )}

                    <div className="flex justify-end pt-4 border-t">
                        <button
                            type="submit"
                            disabled={isLoading || !isDirty}
                            className="px-4 py-2 bg-blue-600 text-white rounded shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 disabled:opacity-50 text-sm font-medium"
                        >
                            {isLoading ? "Saving..." : isEditMode ? "Save Changes" : "Create Code"}
                        </button>
                    </div>
                </form>
            </div>
        </AppContainer>
    );
};

export default InvitationCodeEdit;
